import * as yup from "yup";
import validator from "validator";
import { useEffect, useMemo, useState } from "react";
import { useExtendedFormik } from "../../../../../../hooks/useExtendedFormik";
import { useAppTranslation } from "../../../../../../i18n/useAppTranslation";
import { useCurrentUserQuery } from "../../../../../../api/queries/useUserQuery";
import { useUsersQuery } from "../../../../../../api/queries/useUsersQuery";
import { useEfaxTemplatesQuery } from "../../../../../../api/efax/useEfaxTemplatesQuery";
import { useFavoritesOptions } from "./useFavoritesOptions";

export const useForm = (submitter, clientId) => {
  const { tInbox } = useAppTranslation.Inbox();
  const { data: currentUser } = useCurrentUserQuery();
  const { data: users } = useUsersQuery();
  const { data: templates } = useEfaxTemplatesQuery();
  const [makeDefault, setMakeDefault] = useState(false);

  const allUsers = useMemo(() => {
    return users?.map((user) => ({
      label: `${user.firstname} ${user.lastname}`,
      value: user.id,
    }));
  }, [users]);

  const currentUserSelected = allUsers?.find(
    (user) => user.value === currentUser?.id,
  );

  const favoritesOptions = useFavoritesOptions();

  const noneOption = { label: "None", value: 0 };

  const templateOptions = useMemo(() => {
    const options =
      templates?.templates.map((template) => ({
        label: template.name,
        value: template.id,
      })) || [];
    return [noneOption, ...options];
  }, [templates]);

  const { values, changeField, getError, handleSubmit } = useExtendedFormik({
    enableReinitialize: true,
    initialValues: {
      clinic: null,
      favorites: {
        label: "",
        value: null,
        faxNumber: "",
        templateId: null,
      },
      patient: null,
      template: noneOption,
      faxNumber: "",
      user: currentUserSelected,
      checkedTests: [],
      attachments: [],
    },
    validationSchema: yup.object({
      favorites: yup
        .object({
          label: yup
            .string()
            .required(
              tInbox("efaxOrders.modalAdd.formError.favoritesRequired"),
            ),
          value: yup.number().nullable(),
        })
        .nullable()
        .required(tInbox("efaxOrders.modalAdd.formError.favoritesRequired")),
      clinic: yup
        .object({
          label: yup.string().required(),
          value: yup.number().required(),
        })
        .nullable()
        .required(tInbox("efaxOrders.modalAdd.formError.clinicRequired")),
      patient: !clientId
        ? yup
            .object({
              label: yup.string().required(),
              value: yup.number().required(),
            })
            .nullable()
            .required(tInbox("efaxOrders.modalAdd.formError.patientRequired"))
        : yup
            .object({
              label: yup.string(),
              value: yup.number(),
            })
            .nullable(),
      faxNumber: yup
        .string()
        .required("Missing Fax Number")
        .test("is-numeric", "Fax Number must be a number", (value) =>
          validator.isNumeric(value || ""),
        ),
      template: yup
        .object({
          label: yup.string(),
          value: yup.number().nullable(),
        })
        .nullable(),
      checkedTests: yup.array(),
    }),
    onSubmit: submitter,
  });

  useEffect(() => {
    if (templateOptions && values.favorites.value) {
      const preselectedTemplate = templateOptions?.find(
        (t) => t.value === values.favorites.templateId,
      );
      if (preselectedTemplate) {
        changeField("template", preselectedTemplate);
      } else {
        changeField("template", noneOption);
      }
    }
  }, [values.favorites.value, templateOptions]);

  return {
    submit: handleSubmit,
    patient: {
      value: values.patient,
      onChange: (x) => changeField("patient", x),
      getError: () => getError("patient"),
    },
    favorites: {
      value: values.favorites,
      onChange: (x) => changeField("favorites", x),
      getError: () => getError("favorites"),
      options: favoritesOptions,
    },
    clinic: {
      value: values.clinic,
      onChange: (x) => changeField("clinic", x),
      getError: () => getError("clinic"),
    },
    faxNumber: {
      value: values.faxNumber,
      onChange: (x) => changeField("faxNumber", x),
      getError: () => getError("faxNumber"),
    },
    user: {
      value: values.user,
      options: allUsers,
      onChange: (x) => changeField("user", x),
      getError: () => getError("user"),
    },
    checkedTests: {
      value: values.checkedTests,
      onChange: (x) => changeField("checkedTests", x),
      getError: () => getError("checkedTests"),
    },
    template: {
      value: values.template,
      options: templateOptions,
      onChange: (x) => changeField("template", x),
      getError: () => getError("template"),
    },
    makeDefault: {
      value: makeDefault,
      onChange: () => setMakeDefault((prev) => !prev),
    },
    attachments: {
      value: values.attachments,
      onChange: (x) => changeField("attachments", x),
      getError: () => getError("attachments"),
    },
  };
};
